import React, { useCallback } from 'react'
import { Box, Grid, Divider } from '@mui/material'
import { useIntl } from 'react-intl'

import { TextInput } from '../text-input'
import { DateInput } from '../date-input'
import { SelectInput } from '../select-input'
import { NumberInput } from '../number-input'
import { RatingInput } from '../rating-input'
import { ListInput } from '../list-input'

/**
 * Componente interno que renderiza un campo de formulario individual.
 * Selecciona el componente apropiado basándose en `componentType` o usa un componente personalizado si se proporciona.
 * 
 * @private
 */
const Component = (props) => {
  const { component, defaultComponent, componentType, error, ...entryData } = props
  const ComponentClass = component || defaultComponent(componentType)
  return (
    <ComponentClass
      {...{
        ...entryData,
        error: error,
        helperText: error && entryData.text_invalid,
        sx: { height: '100%' },
      }}
    />
  )
}

/**
 * Componente interno que renderiza una fila de campos del formulario.
 * Una fila puede contener múltiples campos o ser un divisor visual.
 * 
 * @private
 */
const FormPanelBlockItem = ({ defaultComponent, data, errors, showErrors, fieldList, row, i }) => {
  return (
    <Grid container spacing={3} key={i} sx={{ mt: 1.25, mb: 0.125 }}>
      {row.length ? (
        row.map((entry) => {
          const { fromData, ...staticElement } = fieldList[entry.ref]
          const element = {
            ...staticElement,
            ...fromData(data),
          }
          if (element?.hidden) return null
          const { component, componentType, ...entryData } = element || {}
          const error = errors[entryData.name] && showErrors
          return (
            <Grid {...entry.conf} key={entry.ref} sx={{ display: 'flex' }}>
              <Component {...{ component, defaultComponent, componentType, error, ...entryData }} />
            </Grid>
          )
        })
      ) : (
        <Divider sx={{ opacity: 0.6, width: '100%' }} />
      )}
    </Grid>
  )
}

/**
 * Componente interno que renderiza un bloque completo del formulario.
 * Gestiona el contenido prepend/append y renderiza todas las filas de campos.
 * 
 * @private
 */
const FormPanelBlock = ({
  defaultComponent,
  block,
  data,
  errors,
  showErrors,
  combos,
  fieldList,
  extraContent,
}) => {
  const intl = useIntl()
  return (
    <Box display="block" sx={{ width: '100%' }}>
      {block.prepend ? block.prepend({ data, combos, intl }) : null}
      {block.items.map((row, i) => {
        return (
          <FormPanelBlockItem
            key={i}
            {...{ defaultComponent, data, errors, showErrors, fieldList, row, i }}
          />
        )
      })}
      {block.append ? block.append({ data, combos, intl }) : null}
      {extraContent}
    </Box>
  )
}

/**
 * @typedef {object} FieldEntry
 * @property {string} ref - La referencia al campo en `fieldList`.
 * @property {object} conf - La configuración de Grid de Material-UI para el campo (ej. `{ xs: 12, md: 6 }`).
 */

/**
 * @typedef {object} FieldDefinition
 * @property {'text' | 'date' | 'select' | 'number' | 'rating' | 'list' | 'text_area'} [componentType] - El tipo de componente a renderizar.
 * @property {React.ComponentType} [component] - Un componente personalizado a usar en lugar del componente por defecto.
 * @property {function(object): object} fromData - Una función que recibe los datos del formulario y devuelve las props dinámicas para el campo.
 * @property {string} [text_invalid] - El mensaje de error a mostrar cuando el campo tiene un error de validación.
 */

/**
 * @typedef {object} BlockDefinition
 * @property {FieldEntry[][]} items - Un array de filas, donde cada fila es un array de entradas de campo. Una fila vacía `[]` renderiza un divisor.
 * @property {function({ data: object, combos: object, intl: object }): React.ReactNode} [prepend] - Una función que devuelve contenido a renderizar antes de los campos.
 * @property {function({ data: object, combos: object, intl: object }): React.ReactNode} [append] - Una función que devuelve contenido a renderizar después de los campos.
 */

/**
 * @typedef {object} FormPanelProps
 * @property {object} data - Los datos del formulario. Se pasa a las funciones `fromData` de cada campo.
 * @property {object} combos - Los datos de combos/opciones para campos de selección. Disponible en funciones `prepend` y `append`.
 * @property {BlockDefinition} block - La definición del bloque que especifica la estructura y contenido del formulario.
 * @property {Object.<string, FieldDefinition>} fieldList - Un objeto que mapea referencias de campo a sus definiciones.
 * @property {Object.<string, boolean>} [errors={}] - Un objeto que mapea nombres de campo a su estado de error (`true` si hay error).
 * @property {boolean} [showErrors] - Si es `true`, muestra los mensajes de error en los campos con errores.
 * @property {React.ReactNode} [extraContent] - Contenido adicional a renderizar al final del formulario.
 */

/**
 * Un componente de panel de formulario dinámico y altamente configurable.
 * 
 * Renderiza formularios basándose en una estructura de configuración declarativa, permitiendo
 * la creación de layouts complejos con múltiples filas y columnas. Soporta diferentes tipos
 * de campos (texto, fecha, select, número, rating, lista) y permite componentes personalizados.
 * 
 * Los campos se definen en `fieldList` con una función `fromData` que extrae las props dinámicas
 * de los datos del formulario, permitiendo una separación clara entre la definición estática
 * del campo y su estado dinámico.
 * 
 * **Nota:** Este componente requiere `react-intl` para la internacionalización en funciones `prepend` y `append`.
 * 
 * @component
 * @example
 * const fieldList = {
 *   firstName: {
 *     componentType: 'text',
 *     fromData: (data) => ({
 *       name: 'firstName',
 *       title: 'Nombre',
 *       value: data.firstName,
 *       onChange: handleChange,
 *     }),
 *     text_invalid: 'El nombre es requerido',
 *   },
 *   birthDate: {
 *     componentType: 'date',
 *     fromData: (data) => ({
 *       name: 'birthDate',
 *       title: 'Fecha de Nacimiento',
 *       value: data.birthDate,
 *       onChange: handleChange,
 *     }),
 *   },
 * };
 * 
 * const blockConfig = {
 *   items: [
 *     [
 *       { ref: 'firstName', conf: { xs: 12, md: 6 } },
 *       { ref: 'birthDate', conf: { xs: 12, md: 6 } },
 *     ],
 *     [], // Divisor
 *   ],
 *   prepend: ({ data }) => <Typography>Datos de: {data.firstName}</Typography>,
 * };
 * 
 * <FormPanel
 *   data={formData}
 *   combos={combosData}
 *   block={blockConfig}
 *   fieldList={fieldList}
 *   errors={{ firstName: true }}
 *   showErrors={true}
 * />
 * 
 * @param {FormPanelProps} props - Las props del componente FormPanel.
 */
export const FormPanel = ({
  data,
  combos,
  block,
  fieldList,
  errors = {},
  showErrors,
  extraContent,
}) => {
  const defaultComponent = useCallback((type) => {
    switch (type) {
      case 'date':
        return DateInput
      case 'select':
        return SelectInput
      case 'text_area':
        return TextInput
      case 'number':
        return NumberInput
      case 'rating':
        return RatingInput
      case 'list':
        return ListInput
      default:
        return TextInput
    }
  }, [])

  return (
    <FormPanelBlock
      {...{
        defaultComponent,
        block,
        data,
        errors,
        showErrors,
        combos,
        fieldList,
        extraContent,
      }}
    />
  )
}

export default FormPanel
